import { useState } from "react";
import { Button as UiButton } from "@shadcn-in-nx/ui/button";
import { Bluelagoon } from "@bluelagoon";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

export function CancelSubscriptionModal({
    isOpen,
    onClose,
    onCancel,
    onPause,
    onContactSupport,
    currentPlan,
    t,
}) {
    const { user } = useAuth();
    const [step, setStep] = useState("reason"); // "reason", "solution", "confirm", "support_form"
    const [selectedReason, setSelectedReason] = useState(null);
    const [canceling, setCanceling] = useState(false);
    const [supportMessage, setSupportMessage] = useState("");
    const [sendingMessage, setSendingMessage] = useState(false);
    const [messageSent, setMessageSent] = useState(false);

    const reasons = [
        {
            id: "project_over",
            label:
                t("billing.cancel.projectOver") ||
                "Nothing went wrong. My project is over.",
            icon: "✅",
            solution: "resources",
        },
        {
            id: "missing_features",
            label:
                t("billing.cancel.missingFeatures") ||
                "I am missing some functionality",
            icon: "🔧",
            solution: "support",
        },
        {
            id: "using_another",
            label:
                t("billing.cancel.usingAnother") || "I'm using another service",
            icon: "🔄",
            solution: "compare",
        },
        {
            id: "not_using",
            label: t("billing.cancel.notUsing") || "I don't use it enough",
            icon: "⏸️",
            solution: "pause",
        },
        {
            id: "too_expensive",
            label: t("billing.cancel.tooExpensive") || "It's too expensive",
            icon: "💰",
            solution: "consulting",
        },
        {
            id: "technical_issues",
            label:
                t("billing.cancel.technicalIssues") ||
                "I'm having technical issues",
            icon: "🐛",
            solution: "support",
        },
        {
            id: "too_difficult",
            label: t("billing.cancel.tooDifficult") || "It's too difficult",
            icon: "😓",
            solution: "support",
        },
    ];

    const handleReasonSelect = (reason) => {
        setSelectedReason(reason);
        setStep("solution");
    };

    const handlePause = async () => {
        try {
            setCanceling(true);
            await onPause?.();
            onClose();
        } catch (error) {
            console.error("Error pausing subscription:", error);
        } finally {
            setCanceling(false);
        }
    };

    const handleContactSupport = () => {
        setStep("support_form");
    };

    const handleSendMessage = async () => {
        if (!supportMessage.trim()) {
            toast.error(t("billing.support.messageRequired") || "Please enter a message");
            return;
        }

        try {
            setSendingMessage(true);
            const response = await Bluelagoon.sendSupportMessage({
                userId: user.id,
                message: supportMessage,
                context: "cancellation",
            });

            if (response?.status === 200) {
                setMessageSent(true);
                setTimeout(() => {
                    onClose();
                }, 3000);
            } else {
                toast.error(
                    t("billing.support.sendError") ||
                        "Failed to send message. Please try again."
                );
            }
        } catch (error) {
            console.error("Error sending support message:", error);
            toast.error("Failed to send message. Please try again.");
        } finally {
            setSendingMessage(false);
        }
    };

    const handleConfirmCancel = async () => {
        try {
            setCanceling(true);
            await onCancel?.(selectedReason?.id);
            onClose();
        } catch (error) {
            console.error("Error canceling subscription:", error);
        } finally {
            setCanceling(false);
        }
    };

    const renderSolution = () => {
        switch (selectedReason?.solution) {
            case "pause":
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="text-4xl mb-4">⏸️</div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                {t("billing.cancel.pauseTitle") ||
                                    "Take a Break Instead?"}
                            </h3>
                            <p className="text-gray-600">
                                {t("billing.cancel.pauseDescription") ||
                                    "Pause your subscription for up to 3 months. Your data will be safe and you can resume anytime."}
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <UiButton
                                onClick={handlePause}
                                disabled={canceling}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                {t("billing.cancel.pauseFor3Months") ||
                                    "Pause for 3 months"}
                            </UiButton>
                            <UiButton
                                onClick={() => setStep("confirm")}
                                variant="outline"
                                disabled={canceling}
                            >
                                {t("billing.cancel.stillCancel") ||
                                    "Still cancel"}
                            </UiButton>
                        </div>
                    </div>
                );

            case "consulting":
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="text-4xl mb-4">👨‍💻</div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                {t("billing.cancel.consultingTitle") ||
                                    "Get Free Expert Help!"}
                            </h3>
                            <p className="text-gray-600 mb-4">
                                {t("billing.cancel.consultingDescription") ||
                                    "Before you go, let us help you get the most value from AgentUI."}
                            </p>
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-4">
                                <div className="text-3xl font-bold text-blue-600 mb-2">
                                    3 Hours Free Consulting
                                </div>
                                <div className="text-sm text-gray-600">
                                    {t("billing.cancel.consultingValue") ||
                                        "With our lead engineer (valued at $375)"}
                                </div>
                                <ul className="mt-4 text-left space-y-2 text-sm">
                                    <li className="flex items-start gap-2">
                                        <span className="text-blue-600">✓</span>
                                        <span>
                                            {t(
                                                "billing.cancel.consulting1"
                                            ) ||
                                                "Optimize your current workflows"}
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-blue-600">✓</span>
                                        <span>
                                            {t(
                                                "billing.cancel.consulting2"
                                            ) ||
                                                "Build custom solutions for your needs"}
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-blue-600">✓</span>
                                        <span>
                                            {t(
                                                "billing.cancel.consulting3"
                                            ) ||
                                                "Get personalized training and support"}
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <UiButton
                                onClick={handleContactSupport}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                {t("billing.cancel.claimConsulting") ||
                                    "Claim Free Consulting"}
                            </UiButton>
                            <UiButton
                                onClick={() => setStep("confirm")}
                                variant="outline"
                                disabled={canceling}
                            >
                                {t("billing.cancel.stillCancel") ||
                                    "Still cancel"}
                            </UiButton>
                        </div>
                    </div>
                );

            case "support":
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="text-4xl mb-4">💬</div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                {t("billing.cancel.supportTitle") ||
                                    "Let Us Help You!"}
                            </h3>
                            <p className="text-gray-600">
                                {t("billing.cancel.supportDescription") ||
                                    "Our team is here to help resolve any issues and make sure you get the most out of AgentUI."}
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <UiButton
                                onClick={handleContactSupport}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                {t("billing.cancel.contactSupport") ||
                                    "Talk to Support"}
                            </UiButton>
                            <UiButton
                                onClick={() => setStep("confirm")}
                                variant="outline"
                                disabled={canceling}
                            >
                                {t("billing.cancel.stillCancel") ||
                                    "Still cancel"}
                            </UiButton>
                        </div>
                    </div>
                );

            case "resources":
            case "compare":
            default:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="text-4xl mb-4">
                                {selectedReason?.icon}
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                {t("billing.cancel.weSeeTitle") ||
                                    "We Understand"}
                            </h3>
                            <p className="text-gray-600">
                                {t("billing.cancel.thankYouMessage") ||
                                    "Thank you for trying AgentUI. We'd love to have you back anytime."}
                            </p>
                        </div>

                        <UiButton
                            onClick={handleConfirmCancel}
                            disabled={canceling}
                            className="w-full bg-red-600 hover:bg-red-700 text-white"
                        >
                            {canceling
                                ? t("billing.cancel.canceling") ||
                                  "Canceling..."
                                : t("billing.cancel.confirmCancel") ||
                                  "Confirm Cancellation"}
                        </UiButton>
                    </div>
                );
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-2xl font-bold text-gray-900">
                        {step === "reason"
                            ? t("billing.cancel.whatWentWrong") ||
                              "What went wrong?"
                            : step === "solution"
                            ? t("billing.cancel.beforeYouGo") ||
                              "Before you go..."
                            : step === "support_form"
                            ? t("billing.support.title") || "Contact Support"
                            : t("billing.cancel.confirmTitle") ||
                              "Confirm Cancellation"}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {step === "reason" && (
                        <div className="space-y-4">
                            <p className="text-gray-600 mb-6">
                                {t("billing.cancel.helpUsImprove") ||
                                    "Help us improve by letting us know why you're canceling:"}
                            </p>

                            <div className="grid gap-3">
                                {reasons.map((reason) => (
                                    <button
                                        key={reason.id}
                                        onClick={() =>
                                            handleReasonSelect(reason)
                                        }
                                        className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                                    >
                                        <span className="text-2xl">
                                            {reason.icon}
                                        </span>
                                        <span className="font-medium text-gray-900">
                                            {reason.label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === "solution" && renderSolution()}

                    {step === "confirm" && (
                        <div className="space-y-6">
                            <div className="text-center">
                                <div className="text-4xl mb-4">😢</div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">
                                    {t("billing.cancel.sureTitle") ||
                                        "Are you sure?"}
                                </h3>
                                <p className="text-gray-600 mb-4">
                                    {t("billing.cancel.sureDescription") ||
                                        "You will lose access to all your apps, workflows, and data."}
                                </p>
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                                    <p className="text-sm text-gray-700">
                                        {t("billing.cancel.currentPlan") ||
                                            "Current Plan"}
                                        :{" "}
                                        <span className="font-semibold">
                                            {currentPlan}
                                        </span>
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <UiButton
                                    onClick={onClose}
                                    variant="outline"
                                    disabled={canceling}
                                >
                                    {t("billing.cancel.keepSubscription") ||
                                        "Keep My Subscription"}
                                </UiButton>
                                <UiButton
                                    onClick={handleConfirmCancel}
                                    disabled={canceling}
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                >
                                    {canceling
                                        ? t("billing.cancel.canceling") ||
                                          "Canceling..."
                                        : t("billing.cancel.confirmCancel") ||
                                          "Confirm Cancellation"}
                                </UiButton>
                            </div>
                        </div>
                    )}

                    {step === "support_form" && (
                        <div className="space-y-6">
                            {!messageSent ? (
                                <>
                                    <div className="text-center">
                                        <div className="text-4xl mb-4">💬</div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                                            {t("billing.support.formTitle") ||
                                                "How can we help?"}
                                        </h3>
                                        <p className="text-gray-600 mb-4">
                                            {t("billing.support.formDescription") ||
                                                "Our team will get back to you as soon as possible."}
                                        </p>
                                    </div>

                                    {/* Client Details */}
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
                                        <div className="font-semibold text-gray-700 mb-2">
                                            {t("billing.support.yourDetails") ||
                                                "Your Details:"}
                                        </div>
                                        <div className="space-y-1 text-gray-600">
                                            <div>
                                                <span className="font-medium">
                                                    {t("billing.support.name") || "Name"}:
                                                </span>{" "}
                                                {user?.firstName} {user?.lastName}
                                            </div>
                                            <div>
                                                <span className="font-medium">
                                                    {t("billing.support.email") || "Email"}:
                                                </span>{" "}
                                                {user?.email}
                                            </div>
                                            <div>
                                                <span className="font-medium">
                                                    {t("billing.support.plan") || "Plan"}:
                                                </span>{" "}
                                                {currentPlan}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Message Input */}
                                    <div>
                                        <label
                                            htmlFor="support-message"
                                            className="block text-sm font-medium text-gray-700 mb-2"
                                        >
                                            {t("billing.support.messageLabel") ||
                                                "Your Message"}
                                        </label>
                                        <textarea
                                            id="support-message"
                                            value={supportMessage}
                                            onChange={(e) =>
                                                setSupportMessage(e.target.value)
                                            }
                                            placeholder={
                                                t("billing.support.messagePlaceholder") ||
                                                "Tell us more about how we can help..."
                                            }
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                            rows={6}
                                            disabled={sendingMessage}
                                        />
                                    </div>

                                    {/* Send Button */}
                                    <UiButton
                                        onClick={handleSendMessage}
                                        disabled={sendingMessage || !supportMessage.trim()}
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                        {sendingMessage
                                            ? t("billing.support.sending") || "Sending..."
                                            : t("billing.support.sendMessage") || "Send Message"}
                                    </UiButton>

                                    {/* OR Divider */}
                                    <div className="relative">
                                        <div className="absolute inset-0 flex items-center">
                                            <div className="w-full border-t border-gray-300"></div>
                                        </div>
                                        <div className="relative flex justify-center text-sm">
                                            <span className="px-4 bg-white text-gray-500 font-medium">
                                                {t("billing.support.or") || "OR"}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Book Meeting Button */}
                                    <a
                                        href="https://calendar.app.google/r1NEgK7oWPMoUxur9"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block w-full text-center bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold py-3 px-4 rounded-lg transition-colors"
                                    >
                                        📅{" "}
                                        {t("billing.support.bookMeeting") ||
                                            "Book a Meeting with Support"}
                                    </a>
                                </>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="text-6xl mb-4">✅</div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                                        {t("billing.support.messageSent") ||
                                            "Message Sent!"}
                                    </h3>
                                    <p className="text-gray-600">
                                        {t("billing.support.messageSentDescription") ||
                                            "We'll get back to you shortly."}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
